/**
 * Login-Maske: E-Mail eingeben, Magic-Link wird per Mail verschickt.
 */
import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Scenery } from '@/components/scenery';
import { signIn, authClient } from '@/lib/auth';

const LoginSchema = z.object({
  email: z.string().email('Bitte gib eine gültige E-Mail-Adresse ein.'),
});
type LoginInput = z.infer<typeof LoginSchema>;

export const Route = createFileRoute('/login')({
  validateSearch: (s) => {
    const r = (s.redirect as string) ?? '/lehrer';
    // Niemals auf die Anmeldeseite selbst zurückleiten — sonst landet man nach
    // erfolgreicher Magic-Link-Prüfung wieder im Formular.
    return { redirect: r === '/login' || r.startsWith('/login?') ? '/lehrer' : r };
  },

  // Wer schon eine gültige Sitzung hat, sieht das Formular gar nicht erst.
  // Ohne diese Prüfung zeigt die Seite nach erfolgreichem Login weiterhin das
  // Formular, bis manuell neu geladen wird — Nutzer halten das für einen Fehler.
  // (Sicherheitsprüfung 19.08.2026)
  beforeLoad: async ({ search }) => {
    let hasSession = false;
    try {
      const { data } = await authClient.getSession();
      hasSession = Boolean(data?.session);
    } catch {
      // API nicht erreichbar → Formular anzeigen statt zu blockieren
    }
    if (hasSession) {
      throw redirect({ to: search.redirect });
    }
  },

  component: LoginScreen,
});

function LoginScreen() {
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();
  const search = Route.useSearch();

  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: LoginInput) {
    try {
      const { error } = await signIn.magicLink({
        email: values.email,
        callbackURL: search.redirect,
      });
      if (error) {
        toast.error(error.status === 429 ? 'Zu viele Versuche' : 'Login nicht möglich', {
          description:
            error.status === 429
              ? 'Bitte eine Minute warten und dann nochmal versuchen.'
              : (error.message ?? 'Bitte später erneut versuchen.'),
          duration: 8000,
        });
        return;
      }
      setSent(true);
    } catch (e) {
      toast.error('Verbindungsfehler', {
        description: 'API nicht erreichbar. Ist die NucBox online?',
      });
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
      <Scenery />
      <Card className="relative z-10 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-3xl font-bold text-white shadow-[0_4px_0_rgba(37,84,199,0.2)]">
            A
          </div>
          <CardTitle className="text-2xl">Schreibhilfe</CardTitle>
          <CardDescription>
            Magic-Link-Login für Lehrer:innen
          </CardDescription>
        </CardHeader>

        <CardContent>
          {sent ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-success" />
              <h3 className="text-lg font-bold text-gray-800">Mail ist unterwegs!</h3>
              <p className="mt-2 text-sm text-gray-500">
                Klick den Link in der E-Mail an{' '}
                <strong className="text-gray-700">{form.getValues('email')}</strong> an. Der Link
                ist 15 Minuten gültig.
              </p>
              <Button
                variant="ghost"
                onClick={() => {
                  setSent(false);
                  form.reset();
                }}
                className="mt-4"
              >
                Andere E-Mail-Adresse
              </Button>
            </div>
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Deine E-Mail-Adresse</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="name@schule.at"
                    className="pl-10"
                    autoFocus
                    {...form.register('email')}
                  />
                </div>
                {form.formState.errors.email && (
                  <p className="text-sm text-danger">{form.formState.errors.email.message}</p>
                )}
              </div>
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> wird verschickt…
                  </>
                ) : (
                  'Login-Link schicken'
                )}
              </Button>
              <p className="text-center text-xs text-gray-500">
                Kein Passwort nötig: Du bekommst einen Login-Link per Mail. Anmelden können nur
                freigeschaltete Adressen.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
